<template>
  <editor-content :editor="editor" />
</template>

<script>
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import Image from 'shared/components/editor-image';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import TrailingNode from 'shared/components/trailing-node';
import Underline from '@tiptap/extension-underline';
import Video from 'shared/components/editor-video';
import useStorage from 'shared/services/storage';
import { DOMParser } from 'prosemirror-model';
import { EVENT_BUS, FIREBASE_APP, USER_SERVICE } from 'shared/constants';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { Link } from '@tiptap/extension-link';
import { Youtube } from '@tiptap/extension-youtube';
import { inject } from 'vue';
import { parseHTML } from 'hostic-dom';

export default {
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    initialContent: {
      type: [Object, String],
      default: () => null,
    },
    placeholder: {
      type: String,
      default: 'Add content here...',
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    canUploadImages: {
      type: Boolean,
      default: false,
    },
  },
  components: { EditorContent },

  emits: ['update:value'],

  setup(props, { emit }) {
    const firebaseApp = inject(FIREBASE_APP);
    const userService = inject(USER_SERVICE);
    const eventBus = inject(EVENT_BUS);

    const { uploadScreenshot, uploadFile } = useStorage(
      firebaseApp.storage,
      userService.currentUser,
    );

    const editor = useEditor({
      content: props.initialContent,
      extensions: [
        Document,
        Paragraph,
        Youtube,
        Image({
          uploadFn: uploadScreenshot,
          hasStorage: props.canUploadImages,
          imageTypes: [
            'image/png',
            'image/jpg',
            'image/jpeg',
            'image/bmp',
            'image/gif',
            'image/svg',
          ],
          eventBus,
        }),
        Video({
          uploadFn: uploadFile,
          hasStorage: props.canUploadImages,
          fileTypes: [
            'video/mp4',
            'video/mov',
            'video/webm',
            'video/mkv',
            'video/amv',
          ],
          eventBus,
        }),
        Video({
          uploadFn: uploadScreenshot,
          hasStorage: props.canUploadImages,
          fileTypes: [
            'video/mp4',
            'video/mov',
            'video/webm',
            'video/mkv',
            'video/amv',
          ],
          eventBus,
        }),
        Text,
        BulletList,
        Heading.configure({ levels: [1, 2, 3] }),
        Dropcursor,
        ListItem,
        OrderedList,
        Link,
        Bold,
        Code,
        CodeBlock,
        Strike,
        Underline,
        History,
        Placeholder.configure({ placeholder: props.placeholder }),
        Italic,
        TrailingNode,
      ],
      onUpdate: ({ editor }) => {
        emit('update:value', editor.getJSON());
      },
    });

    return { editor };
  },
  methods: {
    addImage({ src, name, size }) {
      console.log(this.editor.commands);
      this.editor.commands.setImage({ src, name, size });
    },
    addVideo({ src, name, size }) {
      this.editor.commands.setVideo({ src, name, size });
    },
    insertContent(text) {
      this.editor.commands.insertContent(text);
    },
    parseHTML(html) {
      const dom = parseHTML(html);
      return DOMParser.fromSchema(this.editor.schema).parse(dom).toJSON();
    },
    setContent(content) {
      this.editor.commands.setContent(content);
      this.$emit('update:value', content);
    },
  },
  mounted() {
    if (this.autoFocus) {
      this.editor.commands.focus();
    }
    if (this.initialContent !== '') {
      this.$emit('update:value', this.editor.getJSON());
    }
    if (this.value !== null) {
      this.setContent(this.value);
    }
  },
  beforeUnmount() {
    this.editor.destroy();
  },
};
</script>

<style type="text/scss">
</style>
